import type { FormEvent } from "react";
import Button from "../../../../../shared/components/Button";
import PasswordInput from "../../../../../shared/components/PasswordInput";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import type { AuthUser } from "../../../../auth/shared/types/auth";
import ProfileAccordionSection from "../ProfileAccordionSection";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

type ProfileSecurityTabProps = {
  user: AuthUser;
  open: boolean;
  onToggle: () => void;
  securityEmailDraft: string;
  onSecurityEmailDraftChange: (value: string) => void;
  onResetSecurityEmail: () => void;
  onSaveSecurityEmail: (event: FormEvent<HTMLFormElement>) => void;
  hasEmailChange: boolean;
  emailSaving: boolean;
  emailLinkLoading: boolean;
  emailError: string;
  onRequestEmailConfirmationLink: () => void;
  currentPasswordDraft: string;
  onCurrentPasswordDraftChange: (value: string) => void;
  newPasswordDraft: string;
  onNewPasswordDraftChange: (value: string) => void;
  newPasswordConfirmDraft: string;
  onNewPasswordConfirmDraftChange: (value: string) => void;
  onChangePassword: (event: FormEvent<HTMLFormElement>) => void;
  passwordSaving: boolean;
  passwordError: string;
  passwordSuccess: string;
};

export default function ProfileSecurityTab({
  user,
  open,
  onToggle,
  securityEmailDraft,
  onSecurityEmailDraftChange,
  onResetSecurityEmail,
  onSaveSecurityEmail,
  hasEmailChange,
  emailSaving,
  emailLinkLoading,
  emailError,
  onRequestEmailConfirmationLink,
  currentPasswordDraft,
  onCurrentPasswordDraftChange,
  newPasswordDraft,
  onNewPasswordDraftChange,
  newPasswordConfirmDraft,
  onNewPasswordConfirmDraftChange,
  onChangePassword,
  passwordSaving,
  passwordError,
  passwordSuccess,
}: ProfileSecurityTabProps) {
  return (
    <ProfileAccordionSection
      id="profile-security"
      icon="lock"
      title={siteTexts.profile.user.tabs.security}
      subtitle={siteTexts.profile.user.security.description}
      open={open}
      onToggle={onToggle}
    >
      <div className="user-profile-content-section">
        <UserProfileSectionHeader
          icon="lock"
          title={siteTexts.profile.user.tabs.security}
          description={siteTexts.profile.user.security.description}
        />
        <form className="user-profile-form" onSubmit={onSaveSecurityEmail}>
          <h3>{siteTexts.profile.user.security.emailTitle}</h3>
          <label className="field">
            <span>{siteTexts.profile.user.labels.email}</span>
            <input
              type="email"
              value={securityEmailDraft}
              onChange={(event) =>
                onSecurityEmailDraftChange(event.target.value)
              }
              required
            />
            {!!user.pendingEmail && (
              <small>
                {siteTexts.profile.user.security.pendingEmailPrefix}{" "}
                <strong>{user.pendingEmail}</strong>
              </small>
            )}
          </label>

          {!!user.pendingEmail && (
            <div className="user-profile-actions">
              <Button
                type="button"
                variant="pill"
                onClick={onRequestEmailConfirmationLink}
                disabled={emailLinkLoading}
              >
                {emailLinkLoading
                  ? siteTexts.profile.user.security.generatingLink
                  : siteTexts.profile.user.security.confirmEmail}
              </Button>
            </div>
          )}

          {emailError && <p className="status-message">{emailError}</p>}

          <div className="user-profile-actions">
            <Button
              type="button"
              variant="pill"
              onClick={onResetSecurityEmail}
              disabled={emailSaving || !hasEmailChange}
            >
              {siteTexts.profile.user.cancel}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={emailSaving || !hasEmailChange}
            >
              {emailSaving
                ? siteTexts.profile.user.security.emailSaving
                : siteTexts.profile.user.security.updateEmail}
            </Button>
          </div>
        </form>

        <form className="user-profile-form" onSubmit={onChangePassword}>
          <h3>{siteTexts.profile.user.security.passwordTitle}</h3>
          <label className="field">
            <span>{siteTexts.profile.user.security.currentPassword}</span>
            <PasswordInput
              value={currentPasswordDraft}
              onChange={(event) =>
                onCurrentPasswordDraftChange(event.target.value)
              }
              required
            />
          </label>
          <label className="field">
            <span>{siteTexts.profile.user.security.newPassword}</span>
            <PasswordInput
              value={newPasswordDraft}
              onChange={(event) => onNewPasswordDraftChange(event.target.value)}
              minLength={4}
              required
            />
          </label>
          <label className="field">
            <span>{siteTexts.profile.user.security.confirmNewPassword}</span>
            <PasswordInput
              value={newPasswordConfirmDraft}
              onChange={(event) =>
                onNewPasswordConfirmDraftChange(event.target.value)
              }
              minLength={4}
              required
            />
          </label>

          {passwordError && <p className="status-message">{passwordError}</p>}
          {passwordSuccess && (
            <p className="status-message">{passwordSuccess}</p>
          )}

          <div className="user-profile-actions">
            <Button type="submit" variant="primary" disabled={passwordSaving}>
              {passwordSaving
                ? siteTexts.profile.user.security.passwordUpdating
                : siteTexts.profile.user.security.updatePassword}
            </Button>
          </div>
        </form>
      </div>
    </ProfileAccordionSection>
  );
}
